<template>
  <div class="file-drop">
    <switch-view-mode
      v-if="!disableModeSwitch"
      class="file-drop__switch"
      v-bind:value="selectedViewMode"
      v-on:input="switchViewMode"
    />
    <div v-if="!isSelectedTableViewMode" class="file-drop__gallery">
      <ul
        v-if="files.length > 0"
        class="file-drop__gallery-column">
        <li
          v-for="(file, index) in files"
          v-bind:key="index">
          <patient-snapshot
            v-bind:file="file"
            v-on:delete-snapshot="removeFile"
            v-on:show-snapshot="openFile"
            v-on:download-snapshot="downloadFile"
          />
          <div
            v-if="getFileFieldDetails(file).length > 0"
            class="file-drop__error">
            {{ getFileFieldDetails(file)[0] }}
          </div>
        </li>
      </ul>
    </div>
    <div v-if="isSelectedTableViewMode">
      <ul
        v-if="files.length > 0"
        class="file-drop__table-column">
        <li
          v-for="(file, index) in files"
          v-bind:key="index">
          <div
            class="file-drop__row"
            v-bind:class="{ error: isErrorFile(file) }">
            <button
              class="file-drop__image"
              type="button"
              v-on:click="openFile(file)">{{ file.name }}</button>
            <button
              class="file-drop__download"
              type="button"
              v-on:click.stop="downloadFile(file)">Скачать</button>
            <button
              class="file-drop__delete"
              type="button"
              v-on:click.stop="removeFile(file)" />
            <div
              v-if="getFileFieldDetails(file).length > 0"
              class="file-drop__error">
              {{ getFileFieldDetails(file)[0] }}
            </div>
          </div>
        </li>
      </ul>
    </div>
    <div
      class="file-drop__upload"
      v-on:drop="dropFiles"
      v-on:dragover.prevent>
      <button
        class="file-drop__container"
        type="button"
        v-on:click="fileInput.click()">
        <img
          alt="Добавьте файлы"
          class="patient-visits__files__icon-add"
          src="@/assets/img/add-files.svg"
          width="51"
          height="46" />
        <p>
          Перетащите файлы в эту область или нажмите сюда
          <template v-if="hint">({{ hint }})</template>
        </p>
      </button>
    </div>
    <div
      v-if="storageInputError.length > 0"
      class="file-drop__error">
      {{ storageInputError.join("\n ") }}
    </div>
  </div>
</template>
<script>

import PatientSnapshot from '@/components/PatientSnapshot.vue';
import SwitchViewMode, { TABLE_MODE } from '@/components/SwitchViewMode.vue';

const acceptedList = [
  'application/pdf',
  'application/msword',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'application/vnd.msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc',
  '.docx',
  '.jpeg',
  '.jpg',
  '.png',
];

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violationCauses ? vm.violationCauses[cause] : []
);

/**
 * @description Компонент загрузки файлов.
 * @type {any}
 */
export default {
  components: {
    SwitchViewMode,
    PatientSnapshot,
  },
  computed: {
    /**
     * @description Выбран режим отображения "Таблица".
     * @returns {boolean}
     */
    isSelectedTableViewMode() {
      return this.selectedViewMode === TABLE_MODE;
    },
    storageInputError: resolveInputErrors('storage'),
  },
  data() {
    return {
      /**
       * @description Выбранный режим отображения.
       * @type {String}
       */
      selectedViewMode: this.initialViewMode,
      /**
       * @description Поле файла.
       * @type {any}
       */
      fileInput: {},
      /**
       * @description Файлы.
       * @type {any[]}
       */
      files: [],
    };
  },
  methods: {
    /**
     * @description Переключить режим отображения.
     * @param mode
     * @returns {void}
     */
    switchViewMode(mode) {
      this.selectedViewMode = mode;
    },
    /**
     * @description Добавить файлы для загрузки.
     * @param {FileList} files
     */
    addFiles(files) {
      for (let i = 0; i < files.length; i += 1) {
        if (!files[i].type || this.acceptedList.includes(files[i].type)) {
          const url = URL.createObjectURL(files[i]);
          const file = files[i];
          file.snapshotUrl = url;
          this.$emit('upload-file', files[i]);
        }
      }
    },
    /**
     * @description Отправить событие на скачивание файла.
     * @param {any} file
     * @returns {void}
     */
    downloadFile(file) {
      this.$emit('download-file', file);
    },
    /**
     * @description Отправить событие на предпросмотр файла.
     * @param {any} file
     * @returns {void}
     */
    openFile(file) {
      this.$emit('open-file', file);
    },
    isErrorFile(file) {
      return this.errorFiles.includes(file);
    },
    /**
     * @description Обработчик перетаскивания файлов.
     * @param {Event} ev
     * @returns {void}
     */
    dropFiles(ev) {
      ev.preventDefault();
      if (ev.dataTransfer.files) {
        this.addFiles(ev.dataTransfer.files);
      }
    },
    getFileFieldDetails(file) {
      const { name } = file;
      return this.violationCauses[`file_${name}`] || [];
    },
    /**
     * @description Удалить файл.
     * @param {any} file
     * @returns {void}
     */
    removeFile(file) {
      this.$emit('remove-file', file);
    },
    /**
     * @description Установить текущие файлы.
     * @param {any} files Список файлов.
     * @returns {void}
     */
    setFiles(files) {
      if (files) {
        this.files = [...files];
      }
    },
  },
  mounted() {
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = this.acceptedList.join(',');
    this.fileInput.multiple = true;
    this.fileInput.onchange = (event) => {
      const { files } = event.target;
      this.addFiles(files);
    };
  },
  props: {
    /**
     * @description Выключить возможность переключать режимы.
     * @type {Boolean}
     */
    disableModeSwitch: {
      required: false,
      type: Boolean,
      default: () => true,
    },
    /**
     * @description Начальное значение режима отображения.
     * @type {String}
     */
    initialViewMode: {
      required: false,
      type: String,
      default: () => TABLE_MODE,
    },
    acceptedList: { type: Array, default: () => acceptedList },
    errorFiles: { type: Array, required: true },
    hasSavePatient: { type: Boolean },
    inputFiles: { type: Array, required: true },
    violationCauses: { type: Object, required: true },
    hint: { type: String, default: () => 'не более 24 Мб' },
  },
  watch: {
    inputFiles: {
      handler: 'setFiles',
      immediate: true,
    },
  },
};
</script>
<style lang="scss">
@use "@/scss/buttons";
.file-drop {
  &__upload {
    margin-top: 15px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    width: 100%;
    border: 2px dashed #c2cfe0;
    border-radius: 2px;
    padding: 32px;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
  }
  &__switch {
    padding: 20px 0 20px 0;
  }
  &__gallery {
    overflow-y: auto;
  }
  &__gallery-column {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 300px));
    gap: 35px 50px;
    max-height: 615px;
    padding-left: 0 !important;
    list-style: none;
  }
  &__table-column {
    align-self: flex-start;
    display: grid;
    align-items: flex-start;
    grid-template-columns: 1fr;
    gap: 10px;
    list-style: none;
    padding-left: 0 !important;
  }
  &__image {
    text-align: left;
    height: 100%;
  }
  &__row {
    display: grid;
    grid-template-columns: 1fr 100px 16px;
    grid-gap: 20px;
    align-items: center;
    transition: background-color var(--animate-duration);
    cursor: pointer;
    padding: 0 4px;
    &:hover {
      background-color: var(--section-background-color);
    }
  }
  &__delete {
    background-image: url("@/assets/img/exit-grey.svg");
    background-size: contain;
    background-repeat: no-repeat;
    width: 12px;
    height: 12px;
    cursor: pointer;
    &:hover {
      background-image: url("@/assets/img/exit-blue.svg");
    }
  }
  &__download {
    @include buttons.btn-blue(100%, 30px, 16px);
  }
  &__error {
    color: #ff5252;
    padding: 5px;
    white-space: pre-wrap;
  }
  &__container {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    p {
      margin: 18px 0 0 0;
      max-width: 395px;
      color: #90a0b7;
      font-weight: 500;
      font-size: 16px;
      line-height: 19px;
      text-align: center;
    }
  }
}

@media screen and (max-width: 480px) {
  .file-drop {
    &__gallery-column {
      grid-template-columns: repeat(2, minmax(100px, 480px));
      justify-content: center;
      justify-items: center;
      gap: 25px 15px;
      max-height: 300px;
    }
  }
}
</style>
